/**
 * @file
 * On Stage
 */

import * as React from 'react';

// Components
import AnglerWeighIn from '../../components/AnglerWeighIn/AnglerWeighIn';
import BassComponent from '../../components/BassComponent/BassComponent';
import Loader from '../../components/Loader/Loader';
import Message from '../../components/Message/Message';

// Config files
import meta from './meta';

// Utils
import * as api from '../../util/api';

// Types
import {
  AnglerClass,
  IAnglerRankRedis,
  IAnglerTodayWeightRedis,
  isEOD,
  MaybeTournamentRedis,
  RefreshRate,
} from '../../types';

interface IProps {
  // Pretty sure this is the tournament id.
  tid: number;
  // Optional prop. Defaults to [30000, 30000]
  refreshRate?: RefreshRate;
  // Not sure this is really needed. Always seems to be 'On Stage'
  title?: string;
  // active state can come in from prop
  active: boolean;
}

interface IState {
  // Set to true on successful data fetch that is not EOD
  active: boolean;
  // Fetching data indicator
  loading: boolean;
  // Angler Class.
  anglerClass: AnglerClass;
  // Angler TODAYFISHCOUNT param
  count: number;
  // Angler TODAYWEIGHT param
  fishWeight: IAnglerTodayWeightRedis;
  // Angler FONT_NAME param
  name: string;
  // Angler RANK param
  rank: IAnglerRankRedis;
}

// Constants
const REFRESH_RATE: RefreshRate = [5000, 15000];

export class OnStage extends React.Component<IProps, IState> {
  public static defaultProps = {
    refreshRate: REFRESH_RATE,
    title: 'On Stage',
  };

  // @TODO: Completely revamp this
  public state = {
    active: this.props.active, // Active tournament indicator
    anglerClass: 0,
    count: 0,
    fishWeight: {
      '#': '',
      '@': {
        TODAYWEIGHT_OZ: '0',
      },
    },
    loading: true, // Loading indicator
    name: '',
    rank: {
      '#': '0',
      '@': {
        Row_Num: '0',
        TIE: '',
      },
    },
  };

  // We may get full ITournamentRedis data OR ITournamentEODRedis, which has
  // just a single key "REDIS_EOD_VALUE" that indicates end of day
  public fetchData = () => (result: MaybeTournamentRedis) => {
    // If end of day, flag to not active, loading is done
    if (!result || isEOD(result)) {
      return this.setState({
        active: false,
        loading: false,
      });
    }

    const data = result[0];

    return this.setState({
      active: true, // active tournament
      anglerClass: data.CompetitorClass,
      count: data.todays_FishCount,
      fishWeight: {
        '#': data.todays_TotalPdsOzAfterPenalty,
        '@': {
          TODAYWEIGHT_OZ: data.todays_TotalOuncesAfterPenalty,
        },
      },
      loading: false, // loading done
      name: data.FontName,
      rank: {
        '#': data.TieRank,
        '@': {
          Row_Num: data.RowNum,
          TIE: data.TieQnty === 1 ? 'N' : 'Y',
        },
      },
    });
  };

  private apiUri() {
    return `/wp-json/data/v1/tournament/leaderboard/on-stage?tournament_id=${this.props.tid}`;
  }

  public async componentDidMount() {
    // Props show inactive, don't even request
    if (!this.props.active) {
      return this.setState({
        active: this.props.active,
        loading: false,
      });
    }

    // Shared settings
    const apiSettings = {
      callback: this.fetchData(),
      uri: this.apiUri(),
    };

    // Poll if active
    if (this.state.active) {
      await api.subscribe({
        ...apiSettings,
        rate: this.props.refreshRate || REFRESH_RATE,
      });
    }
    // Otherwise make single request
    else {
      await api.get(apiSettings);
    }
  }

  public render() {
    const {
      active,
      anglerClass,
      count,
      fishWeight,
      loading,
      name,
      rank,
    } = this.state;

    return (
      <BassComponent meta={meta}>
        <div className="on-stage leaderboard-list" data-testid="OnStage">
          <h2 className="on-stage__title leaderboard-list__title">
            {this.props.title}
          </h2>

          <Loader loading={loading} />

          {!loading && active && (
            <div className="on-stage__angler">
              <span className="on-stage__sub-title leaderboard-list__sub-title">
                Weighed In
              </span>
              <br />
              <span className="on-stage__angler-link leaderboard-list__angler-link">
                {name}
              </span>
              <br />
              <span className="leaderboard-list__angler-type">
                (
                {anglerClass === AnglerClass.PRO ? 'Pro-Anglers' : 'Co-Anglers'}
                )
              </span>
              <br />
              <span className="on-stage__sub-title leaderboard-list__sub-title">
                Today
              </span>
              <br />
              <AnglerWeighIn
                rank={rank}
                fishCount={count}
                weight={fishWeight}
              />
              <br />
            </div>
          )}

          <Message
            message="Weigh-in has concluded."
            customClass="on-stage__message"
            shouldShow={!loading && !active}
          />
        </div>
      </BassComponent>
    );
  }
}

export default OnStage;
